import { useState, useMemo } from 'react';
import { GifPreview } from '@components/GifPreview';
import { SectionHeader } from './shared';
import styles from '../DemoPage.module.scss';

const DEMO_SITE_ID = 'demo-site-123';

/**
 * Generate a data-URI PNG placeholder at the given dimensions.
 * Draws a grey rectangle with a centered "GIF Preview" label.
 */
const createPlaceholderGif = (width: number, height: number): string => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  // Background
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  // Diagonal stripes for visual interest
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1;
  for (let i = -height; i < width; i += 20) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + height, height);
    ctx.stroke();
  }

  // Center label
  ctx.fillStyle = '#9ca3af';
  ctx.font = `${Math.round(width / 16)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('GIF Preview', width / 2, height / 2);

  return canvas.toDataURL('image/png');
};

export const GifPreviewSection = () => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [cacheBustKey, setCacheBustKey] = useState(0);
  const placeholderUrl = useMemo(() => createPlaceholderGif(320, 213), []);

  const handleRegenerate = () => {
    setIsRegenerating(true);
    setTimeout(() => {
      setIsRegenerating(false);
      setCacheBustKey(Date.now());
    }, 3000);
  };

  return (
    <section className={styles.section}>
      <SectionHeader
        title="GIF Preview"
        description="Hover or click the icon to preview an animated GIF demo. Supports fullscreen, regeneration, and click-to-pin. Used in Manage Leads for onboarded sites."
        styles={styles}
      />

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Default (with regenerate)</h3>
        <div className={styles.infoChipRow}>
          <GifPreview
            siteId={DEMO_SITE_ID}
            onRegenerate={handleRegenerate}
            isRegenerating={isRegenerating}
            cacheBustKey={cacheBustKey}
          />
          <span className={styles.hint}>Hover or click the icon</span>
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Loaded preview (click to fullscreen)</h3>
        <div className={styles.infoChipRow}>
          <GifPreview
            siteId={DEMO_SITE_ID}
            gifUrlOverride={placeholderUrl}
            onRegenerate={handleRegenerate}
            isRegenerating={isRegenerating}
            cacheBustKey={cacheBustKey}
          />
          <span className={styles.hint}>Hover → popover with placeholder. Click image or expand icon → fullscreen</span>
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Without regenerate</h3>
        <div className={styles.infoChipRow}>
          <GifPreview siteId={DEMO_SITE_ID} />
          <span className={styles.hint}>No regenerate button</span>
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Regenerating state</h3>
        <div className={styles.infoChipRow}>
          <GifPreview
            siteId={DEMO_SITE_ID}
            onRegenerate={() => {}}
            isRegenerating
          />
          <span className={styles.hint}>Icon pulses during regeneration</span>
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Error state (invalid site ID)</h3>
        <div className={styles.infoChipRow}>
          <GifPreview
            siteId="non-existent-site"
            onRegenerate={() => alert('Regenerate clicked')}
          />
          <span className={styles.hint}>Shows &quot;Preview unavailable&quot; with regenerate</span>
        </div>
      </div>
    </section>
  );
};
